/* step 04: Search: phone overlay and desktop search. Created by the step 01 gate, Oct 17, 2026. Owner: step 04 only. Guard calls into other steps (if (window.X) ...). */
/*
 * Behaviour for the phone search overlay (#pbs-search, snippets/pbs-search-overlay.liquid).
 * C27: defines <pbs-predictive-search>, a subclass of Dawn's PredictiveSearch, on DOMContentLoaded
 * (predictive-search.js loads last, so the class does not exist when this file first runs).
 * C2 / C7: open and close come from PBSPhone.overlay (pbs-phone.js); this file only listens.
 * Desktop (750px and up) uses Dawn's stock search; nothing here runs for it.
 */
(function () {
  'use strict';

  var OVERLAY_ID = 'pbs-search';

  function defineSearchElement() {
    if (typeof customElements === 'undefined' || customElements.get('pbs-predictive-search')) return;
    // Predictive search switched off in theme settings: Dawn's class is absent, the tag stays a plain wrapper.
    if (typeof PredictiveSearch === 'undefined') return;

    class PBSPredictiveSearch extends PredictiveSearch {
      // Results stay up when the phone keyboard closes; the overlay closes them.
      onFocusOut() {}

      // Dawn caps the results at the space under the header; the overlay scrolls instead.
      open() {
        super.open();
        if (this.predictiveSearchResults) this.predictiveSearchResults.style.maxHeight = '';
      }

      // Dawn's getSearchResults, products only, up to 10, own cache (Dawn's header instances keep theirs).
      getSearchResults(searchTerm) {
        const queryKey = searchTerm.replace(' ', '-').toLowerCase();
        this.setLiveRegionLoadingState();

        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          return;
        }

        fetch(
          `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=10&section_id=predictive-search`,
          { signal: this.abortController.signal }
        )
          .then((response) => {
            if (!response.ok) {
              const error = new Error(response.status);
              this.close();
              throw error;
            }
            return response.text();
          })
          .then((text) => {
            const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
            if (!section) { this.close(); return; }
            const markup = section.innerHTML;
            this.cachedResults[queryKey] = markup;
            this.renderSearchResults(markup);
          })
          .catch((error) => {
            if (error && error.code === 20) return; // aborted
            this.close();
          });
      }

      renderSearchResults(markup) {
        super.renderSearchResults(markup);
        this.highlight();
      }

      // Port of the mockup's hl(): words of 2+ letters except "and", longest first, built as DOM nodes, never innerHTML.
      highlight() {
        const words = this.getQuery().toLowerCase().split(/\s+/)
          .filter((w) => w.length > 1 && w !== 'and')
          .sort((a, b) => b.length - a.length);
        if (!words.length) return;
        const re = new RegExp('(' + words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')', 'ig');
        this.querySelectorAll('[data-pbs-hl]').forEach((el) => {
          const parts = el.textContent.split(re);
          if (parts.length < 2) return;
          el.textContent = '';
          parts.forEach((s, i) => {
            if (!s) return;
            if (i % 2) {
              const m = document.createElement('mark');
              m.textContent = s;
              el.appendChild(m);
            } else {
              el.appendChild(document.createTextNode(s));
            }
          });
        });
      }
    }

    customElements.define('pbs-predictive-search', PBSPredictiveSearch);
  }

  function overlayRoot() { return document.getElementById(OVERLAY_ID); }
  function isPhone() { return !!(window.PBSPhone && typeof window.PBSPhone.isPhone === 'function' && window.PBSPhone.isPhone()); }

  function searchPhone() {
    var opened = false;

    // iOS only raises the keyboard for a focus inside the tap itself. Capture phase: runs before the
    // core's bubbling open handler. The core keeps closed overlays hidden/inert/visibility:hidden,
    // none of which can take focus, so reveal the panel first; open() then runs as usual.
    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var trigger = event.target.closest('[data-pbs-open="search"]');
      if (!trigger || !isPhone() || !window.PBSPhone.overlay) return;
      var overlay = overlayRoot();
      if (!overlay || !overlay.hidden) return; // missing, or already open/closing: nothing to pre-focus
      var input = overlay.querySelector('.pbs-srch__in');
      if (!input) return;
      opened = false;
      overlay.hidden = false;
      overlay.inert = false;
      overlay.removeAttribute('inert');
      overlay.removeAttribute('aria-hidden');
      overlay.style.visibility = 'visible';
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      // If the core declined to open (open() returned false), put the panel back as it was.
      window.setTimeout(function () {
        if (opened) return;
        overlay.hidden = true;
        overlay.inert = true;
        overlay.setAttribute('inert', '');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.style.removeProperty('visibility');
      }, 0);
    }, true);

    document.addEventListener('pbs:overlay:open', function (event) {
      if (!event.detail || event.detail.name !== 'search') return;
      opened = true;
      var overlay = event.detail.overlay || overlayRoot();
      var body = overlay && overlay.querySelector('.pbs-srch__body');
      if (body) body.scrollTop = 0;
    });

    document.addEventListener('pbs:overlay:close', function (event) {
      if (!event.detail || event.detail.name !== 'search') return;
      var overlay = event.detail.overlay || overlayRoot();
      if (overlay) overlay.style.removeProperty('visibility');
    });

    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var overlay = overlayRoot();
      if (!overlay || !overlay.contains(event.target)) return;

      // Chips: fill the field and search at once; focus stays put so the keyboard does not reopen.
      var chip = event.target.closest('[data-pbs-q]');
      if (chip) {
        var input = overlay.querySelector('.pbs-srch__in');
        if (!input) return;
        input.value = chip.getAttribute('data-pbs-q') || chip.textContent.trim();
        var ps = input.closest('pbs-predictive-search');
        if (ps && typeof ps.onChange === 'function') ps.onChange();
        else if (input.form) input.form.submit();
        var body = overlay.querySelector('.pbs-srch__body');
        if (body) body.scrollTop = 0;
        return;
      }

      // Navigation closes the overlay and lets the link go.
      var link = event.target.closest('a[href]');
      if (link && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
        && link.target !== '_blank' && window.PBSPhone && window.PBSPhone.overlay) {
        window.PBSPhone.overlay.close();
      }
    });
  }

  function start() {
    defineSearchElement();
    searchPhone();
  }

  if (document.readyState === 'complete') start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
}());
